import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ModalService} from '../../services/modal.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ApiService} from '../../services/api.service';
import {AuthenticationService} from '../../services/authentication.service';
import {SalesContactService, SalesHead} from '../../services/sales-contact.service';
import {User} from '../../classes/app.models';
import {ToastrService} from 'ngx-toastr';
import {catchError, finalize} from 'rxjs/operators';
import {of} from 'rxjs';

@Component({
    selector: 'indiapicture-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss']
})

export class FooterComponent implements OnInit, OnDestroy {

    currentYear: number = new Date().getFullYear();

    @Input() containerClass = 'container';
    @Input() isBottomSticky = false;
    private enable: boolean = true;

    year: any;
    subscribeForm: FormGroup;

    /**
     * Sales contact for the signed-in customer's region. Null for guests and for
     * regions we have no entry for — the footer then shows the default contact.
     */
    regionContact: SalesHead | null = null;

    /**
     * Whether to render the "Clear cache" link.
     *
     * It is a maintenance action, not a visitor one: the endpoint it calls drops
     * the backend's compiled config AND resets the PHP bytecode cache for the
     * whole worker pool, so every request on the site afterwards recompiles from
     * source. It used to render for signed-out visitors, which put that one
     * curious click away from anyone.
     *
     * The session carries no admin flag today, so this gates on signed-in — an
     * account we can name in the log rather than an anonymous IP. When an admin
     * flag lands, narrow this to it: that is what the link actually wants.
     */
    isSignedIn = false;

    private userSub: Subscription | null = null;

    constructor(private modalService: ModalService, private formBuilder: FormBuilder,
                private apiService: ApiService, private toastService: ToastrService,
                private authService: AuthenticationService,
                private salesContact: SalesContactService) {
    }

    ngOnInit(): void {
        this.subscribeForm = this.formBuilder.group({
            email: ['', Validators.compose([Validators.required])],
        });

        this.year = (new Date()).getFullYear();

        /*
         * Re-resolve on login / logout, otherwise the footer would keep whichever
         * contact was resolved when the page first loaded.
         *
         * currentUser is a BehaviorSubject, so this fires straight away with the
         * current session and there is no separate priming call. It must not be
         * one: currentUserValue emits on read — it calls next(null) when nothing
         * is in storage — so reading it from inside this subscription would
         * re-enter the handler synchronously and recurse until the stack blew,
         * on every page a signed-out visitor loads. The emitted user is the same
         * value without the side effect.
         */
        this.userSub = this.authService.currentUser.subscribe(user => this.applySession(user));
    }

    ngOnDestroy(): void {
        if (this.userSub) { this.userSub.unsubscribe(); }
    }

    /** Everything in the footer that depends on who is signed in. */
    private applySession(user: User | null): void {
        this.applyRegionContact();
        this.isSignedIn = !!user;
    }

    /** Match the stored customer's region against the shared sales-head list. */
    private applyRegionContact(): void {
        this.regionContact = this.salesContact.contactForCurrentUser();
    }

    openCashBack() {
        this.modalService.openCashBack();
    }

    /* ---------- Clear cache ---------- */

    /** True while the request is out, so the link cannot be fired twice. */
    clearingCache = false;

    /**
     * Drop the backend's caches, then this browser's, then reload.
     *
     * Both halves are needed and neither substitutes for the other: the backend
     * can be serving a stale compiled config while the browser holds a stale
     * bundle, and clearing one leaves the other's copy in place.
     */
    clearCache(event: Event): void {
        event.preventDefault();

        if (this.clearingCache) { return; }
        this.clearingCache = true;

        this.apiService.clearBackendCache()
            .pipe(
                // A failure still has to reach the browser half: the local caches
                // are worth clearing whatever the backend said.
                catchError(err => {
                    this.toastService.error('Backend cache could not be cleared.');
                    console.warn('clear-cache failed', err);
                    return of(null);
                }),
                finalize(() => { this.clearingCache = false; })
            )
            .subscribe((res: any) => {
                if (res) {
                    // 207 in the body means some commands ran and some did not —
                    // worth saying, because the stale thing may be the one that
                    // failed.
                    if (res.failed) {
                        this.toastService.warning(
                            res.cleared + ' of ' + (res.cleared + res.failed) + ' backend caches cleared.');
                    } else {
                        this.toastService.success('Backend cache cleared.');
                    }
                }

                this.clearBrowserCache().then(() => window.location.reload());
            });
    }

    /**
     * Empty what this browser is holding, as far as a page is allowed to.
     *
     * Deliberately does NOT touch localStorage or sessionStorage: the session and
     * the signed-in user live there, so clearing them would sign the visitor out
     * — which is not what "clear the cache" is asking for.
     *
     * Every step is optional and independently guarded. Cache Storage and the
     * service worker registry are absent in some browsers and throw outright in a
     * private window, and a failure here must not stop the reload that follows.
     */
    private async clearBrowserCache(): Promise<void> {
        try {
            // Cache Storage — what a service worker serves the app from.
            if (typeof caches !== 'undefined' && caches.keys) {
                const keys = await caches.keys();
                await Promise.all(keys.map(k => caches.delete(k)));
            }
        } catch (e) {
            console.warn('cache storage not cleared', e);
        }

        try {
            /*
             * Unregister the service worker as well as emptying its caches. While
             * it is registered it can answer the next request from a copy it
             * fetches again on its own, so emptying alone does not settle it.
             */
            if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map(r => r.unregister()));
            }
        } catch (e) {
            console.warn('service workers not unregistered', e);
        }
    }

    public onSubmit(values): void {
        if (this.subscribeForm.valid) {
            this.enable = false;

            this.apiService.addSubscribe(values['email']).subscribe(data => {
                this.toastService.success('Thankyou for subscribing to indiapicture.com.');
                this.subscribeForm.reset();
                this.modalService.closeCashBack();
            });
        } else {
            this.enable = true;
        }
    }
}
